import React from 'react';

import { showModal } from 'modal.react.js';
import { GroupBase, InputActionMeta } from 'react-select-5';
import ReactBaseSelect, { PublicBaseSelectProps } from 'react-select-5/base';
import { StateManagerProps } from 'react-select-5/dist/declarations/src/useStateManager';

import { IMasterProperty } from 'api/pages';
import Preview from 'components/Pages/Admin/shared/Preview';
import area from 'components/shared/GlobalModalArea/area';
import Modal from 'components/shared/GlobalModalArea/Modal';
import { useEvent } from 'hooks/useEvent';

const openPagePreview = async (option: IMasterProperty) => {
  await showModal(area, (callback) => (
    <Modal
      noPadding
      size="lg"
      isOpen
      toClose={(event) => {
        event.stopPropagation();
        callback(event);
      }}
      stopMouseDownPropagation
    >
      <Preview pageId={option.id} />
    </Modal>
  ));
};

export const usePreviewModal = function () {
  const [isOpen, select] = React.useState(false);

  const show = React.useCallback<typeof openPagePreview>(async (option) => {
    try {
      select(true);
      await openPagePreview(option);
    } finally {
      select(false);
    }
  }, []);

  return { isOpen, show };
};

export const useStateManager = function <
  Option extends IMasterProperty = IMasterProperty,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  isModalOpenned,
  ...rest
}: StateManagerProps<Option, IsMulti, Group> & { isModalOpenned: boolean }): Partial<
  PublicBaseSelectProps<Option, IsMulti, Group>
> & {
  ref: React.RefObject<ReactBaseSelect<Option, IsMulti, Group>>;
} {
  const selectRef = React.useRef<ReactBaseSelect<Option, IsMulti, Group>>(null);

  const [inputValue, setInputValue] = React.useState('');
  const [menuIsOpen, setMenuIsOpen] = React.useState<boolean>();

  const isModalOpennedPrev = React.useRef(isModalOpenned);
  React.useEffect(() => {
    if (isModalOpennedPrev.current && !isModalOpenned) selectRef.current?.focus();
    isModalOpennedPrev.current = isModalOpenned;
  }, [isModalOpenned]);

  const onInputChange = useEvent((inputValue: string, { action, prevInputValue }: InputActionMeta) => {
    if (action === 'input-change') {
      setInputValue(inputValue);
      return inputValue;
    }

    if (!isModalOpenned) {
      setMenuIsOpen(undefined);
      setInputValue(inputValue);
      return inputValue;
    }

    if (action === 'menu-close') setMenuIsOpen(true);

    setInputValue(prevInputValue);
    return prevInputValue;
  });

  return { ...rest, onInputChange, inputValue, menuIsOpen, ref: selectRef };
};
